import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Calendar, CheckCircle, XCircle, Clock, UserCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ParentAttendance() {
  const navigate = useNavigate();
  const token = localStorage.getItem('parent_token');
  const [students, setStudents] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    fetchChildren();
  }, []);

  useEffect(() => {
    if (selectedId) fetchAttendance(selectedId);
  }, [selectedId]);

  const fetchChildren = async () => {
    try {
      const res = await axios.get(`${API}/parent/children`, { headers: { Authorization: `Bearer ${token}` } });
      const list = res.data.data || [];
      setStudents(list);
      if (list.length > 0) setSelectedId(list[0].student_id);
    } catch {
      toast.error('Failed to load children');
    }
  };

  const fetchAttendance = async (sid) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/parent/student/${sid}/attendance`, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data.data);
    } catch {
      toast.error('Failed to load attendance');
    } finally {
      setLoading(false);
    }
  };

  const summary = data?.summary;
  const records = data?.records || [];

  const statusIcon = (s) => {
    if (s === 'present') return <CheckCircle className="h-4 w-4 text-emerald-500" />;
    if (s === 'absent') return <XCircle className="h-4 w-4 text-red-500" />;
    return <Clock className="h-4 w-4 text-amber-500" />;
  };

  const statusColor = (s) => {
    if (s === 'present') return 'text-emerald-600 bg-emerald-50';
    if (s === 'absent') return 'text-red-600 bg-red-50';
    return 'text-amber-600 bg-amber-50';
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-4 sm:px-8 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Link to="/parent/dashboard" className="text-slate-400 hover:text-slate-700 transition-colors">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-blue-500" />
            <h1 className="font-bold text-slate-900 text-lg">Attendance</h1>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-8 py-6 space-y-6">
        {/* Student selector */}
        {students.length > 1 && (
          <div className="flex gap-3">
            {students.map(s => (
              <button
                key={s.student_id}
                onClick={() => setSelectedId(s.student_id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium transition-colors ${selectedId === s.student_id ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'}`}
              >
                <UserCircle className="h-4 w-4" /> {s.full_name}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 border-4 border-blue-200 border-t-blue-500 rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Summary cards */}
            {summary && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Total Days', value: summary.total, color: 'text-slate-700', bg: 'bg-slate-50' },
                  { label: 'Present', value: summary.present, color: 'text-emerald-700', bg: 'bg-emerald-50' },
                  { label: 'Absent', value: summary.absent, color: 'text-red-700', bg: 'bg-red-50' },
                  { label: 'Attendance %', value: `${summary.percentage}%`, color: 'text-blue-700', bg: 'bg-blue-50' },
                ].map(c => (
                  <div key={c.label} className={`${c.bg} rounded-2xl p-4 text-center`}>
                    <p className={`text-2xl font-bold ${c.color}`}>{c.value}</p>
                    <p className="text-xs text-slate-500 mt-1">{c.label}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Records */}
            {records.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
                <Calendar className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-400">No attendance records found</p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-100 bg-slate-50">
                  <p className="text-sm font-semibold text-slate-600">Recent Records</p>
                </div>
                <div className="divide-y divide-slate-100">
                  {records.map((r, i) => (
                    <div key={i} className="flex items-center justify-between px-5 py-3">
                      <div className="flex items-center gap-3">
                        {statusIcon(r.status)}
                        <span className="text-sm text-slate-700">{r.date}</span>
                      </div>
                      <span className={`text-xs font-medium px-2.5 py-1 rounded-full capitalize ${statusColor(r.status)}`}>
                        {r.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
